import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.12 }
        );
        document.querySelectorAll('.fade-up').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const techServices = [
        {
            icon: '✨',
            title: 'AI & Automation',
            description: 'Our flagship practice. We put AI to work inside your business, cutting manual hours, reducing errors, and helping teams move faster.',
            link: '/ai-services',
            featured: true
        },
        {
            icon: '🌐',
            title: 'Enterprise Web Solutions',
            description: 'Scalable, secure web architectures built for high-availability enterprise and public sector environments.',
            link: '/web-development'
        },
        {
            icon: '💼',
            title: 'Strategic IT Consulting',
            description: 'Technology assessments, digital transformation roadmaps, and compliance guidance.',
            link: '/it-consulting'
        },
        {
            icon: '⚙️',
            title: 'Custom Software Engineering',
            description: 'Tailored software focused on security, efficiency, and verifiable workflows.',
            link: '/software'
        },
        {
            icon: '📱',
            title: 'Mobile Applications',
            description: 'Secure, accessible mobile solutions connecting stakeholders across platforms.',
            link: '/app-development'
        }
    ];

    const investmentServices = [
        {
            icon: '🏠',
            title: 'Real Estate Intelligence',
            description: 'Data-driven property market analysis and investment risk assessment.',
            link: '/real-estate'
        },
        {
            icon: '📈',
            title: 'Strategic Capital Consulting',
            description: 'Portfolio diversification and emerging market analysis for institutional growth.',
            link: '/investing'
        }
    ];

    const features = [
        {
            icon: '🏆',
            title: 'Certified Expertise',
            description: 'Industry-certified professionals dedicated to excellence and compliance.'
        },
        {
            icon: '⚡',
            title: 'Rapid Deployment',
            description: 'Agile delivery models ensuring fast time-to-value for mission-critical projects.'
        },
        {
            icon: '🛡️',
            title: 'Security First',
            description: 'Security embedded at every layer, ensuring robust protection for all solutions.'
        },
        {
            icon: '💡',
            title: 'Scalable Innovation',
            description: 'Future-proof architectures designed to grow with your organizational needs.'
        }
    ];

    return (
        <div className="home">

            {/* ── HERO ── */}
            <section className="hero-pro">
                <div
                    className="hero-pro-bg-image"
                    style={{
                        backgroundImage: `url(${process.env.PUBLIC_URL}/hero-image.png)`,
                        transform: `translateY(${scrollY * 0.4}px)`
                    }}
                />
                <div className="hero-pro-overlay" />
                <div className="hero-pro-bg">
                    <div
                        className="hero-pro-grid"
                        style={{ transform: `translateY(${scrollY * 0.12}px)` }}
                    />
                    <div
                        className="hero-pro-orb hero-pro-orb-1"
                        style={{ transform: `translateY(${scrollY * 0.22}px)` }}
                    />
                    <div
                        className="hero-pro-orb hero-pro-orb-2"
                        style={{ transform: `translateY(${scrollY * 0.14}px)` }}
                    />
                    <div
                        className="hero-pro-orb hero-pro-orb-3"
                        style={{ transform: `translateY(${scrollY * 0.08}px)` }}
                    />
                </div>

                <div className="hero-pro-inner">
                    <div
                        className="hero-pro-content"
                        style={{ transform: `translateY(${scrollY * -0.07}px)` }}
                    >
                        <div className="hero-pro-badge">Trusted Technology Partner</div>
                        <h1 className="hero-pro-title">
                            Secure, Scalable<br />
                            <span className="hero-pro-accent">Technology</span> Solutions
                        </h1>
                        <p className="hero-pro-subtitle">
                            Empowering enterprise and public sector growth through reliable
                            innovation, verifiable compliance, and mission-critical technology.
                        </p>
                        <div className="hero-pro-actions">
                            <Link to="/contact" className="btn btn-primary btn-lg">
                                Start a Conversation →
                            </Link>
                            <a href="#services" className="hero-pro-link">
                                Explore Services ↓
                            </a>
                        </div>
                    </div>

                </div>

                <div className="hero-pro-scroll-hint">
                    <div className="hero-pro-mouse">
                        <div className="hero-pro-mouse-wheel" />
                    </div>
                </div>
            </section>

            {/* ── TECH SERVICES ── */}
            <section className="section" id="services">
                <div className="container">
                    <div className="section-header fade-up">
                        <div className="section-label">What We Do</div>
                        <h2>Enterprise <span className="text-gradient">Technology</span></h2>
                        <p>Comprehensive solutions designed to accelerate mission-critical objectives and digital transformation.</p>
                    </div>
                    <div className="services-grid-pro">
                        {techServices.map((service, i) => (
                            <Link
                                to={service.link}
                                key={i}
                                // If this service is marked `featured: true` (the AI card),
                                // add the extra class that styles it as the highlighted,
                                // full-width flagship card. All others get the plain style.
                                className={`service-card-pro fade-up${service.featured ? ' service-card-pro-featured' : ''}`}
                                style={{ transitionDelay: `${i * 0.07}s` }}
                            >
                                {/* Only the featured card shows the "Flagship" pill badge. */}
                                {service.featured && <span className="scp-flag">Flagship</span>}
                                <div className="scp-icon">{service.icon}</div>
                                <div className="scp-body">
                                    <h3>{service.title}</h3>
                                    <p>{service.description}</p>
                                </div>
                                <span className="scp-arrow">→</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── INVESTMENT SERVICES ── */}
            <section className="section section-navy" style={{ backgroundImage: `linear-gradient(rgba(5,10,25,0.88) 0%, rgba(5,10,25,0.80) 100%), url(${process.env.PUBLIC_URL}/images/earth-night.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="container">
                    <div className="section-header fade-up">
                        <div className="section-label" style={{ color: 'rgba(56,189,248,0.9)' }}>Investment Services</div>
                        <h2 style={{ color: 'white' }}>Infrastructure &amp; <span style={{ background: 'linear-gradient(135deg,#0284c7,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Investment</span></h2>
                        <p style={{ color: 'rgba(255,255,255,0.55)' }}>
                            Strategic consulting for blockchain, digital assets, real estate, and emerging market diversification.
                        </p>
                    </div>
                    <div className="invest-grid">
                        {investmentServices.map((service, i) => (
                            <Link
                                to={service.link}
                                key={i}
                                className="invest-card fade-up"
                                style={{ transitionDelay: `${i * 0.1}s` }}
                            >
                                <div className="invest-card-icon">{service.icon}</div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                                <span className="invest-card-link">Learn More →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── WHY US ── */}
            <section className="section" style={{ backgroundImage: `linear-gradient(rgba(8,15,30,0.92) 0%, rgba(8,15,30,0.92) 100%), url(${process.env.PUBLIC_URL}/images/diverse-team.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center top' }}>
                <div className="container">
                    <div className="why-us-grid">
                        <div className="why-us-left fade-up">
                            <div className="section-label">Why OnOneGroup</div>
                            <h2>Built for <span className="text-gradient">Mission-Critical</span> Work</h2>
                            <p>
                                We combine deep technical expertise with rigorous compliance standards,
                                delivering solutions that enterprise and public sector organizations can trust and rely on.
                            </p>
                            <Link to="/contact" className="btn btn-primary btn-lg" style={{ marginTop: '2rem', display: 'inline-flex' }}>
                                Partner With Us →
                            </Link>
                        </div>
                        <div className="why-us-right">
                            {features.map((f, i) => (
                                <div key={i} className="why-item fade-up" style={{ transitionDelay: `${i * 0.09}s` }}>
                                    <div className="why-item-icon">{f.icon}</div>
                                    <div>
                                        <h4>{f.title}</h4>
                                        <p>{f.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="section">
                <div className="container">
                    <div className="cta-pro fade-up">
                        <div className="cta-pro-glow" />
                        <div className="cta-pro-content">
                            <div className="section-label" style={{ color: 'rgba(56,189,248,0.8)', marginBottom: '1rem' }}>Get Started</div>
                            <h2>Ready to Transform<br />Your Operations?</h2>
                            <p>Talk to our enterprise solutions team about your specific requirements.</p>
                            <div className="cta-pro-actions">
                                <Link to="/contact" className="btn btn-primary btn-lg">Contact Us Today →</Link>
                                <Link to="/about" className="btn btn-ghost btn-lg">Learn About Us</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Home;
